// Shopifyの商品メタフィールドと既存Swiperを、モックの共通調光処理へ接続する。
(() => {
  const DEFAULT_OPACITY = 0.66;

  class PhotochromicProduct extends HTMLElement {
    connectedCallback() {
      if (this.cleanup) return;
      const cleanups = [];
      let disposed = false;
      this.cleanup = () => {
        disposed = true;
        cleanups.reverse().forEach(cleanup => cleanup());
        this.cleanup = null;
      };
      const listen = (target, event, handler) => {
        target.addEventListener(event, handler);
        cleanups.push(() => target.removeEventListener(event, handler));
      };

      const initialize = async () => {
        const gallery = this.closest('.c-productGallery');
        const container = gallery?.querySelector('#norda_slider1');
        const slide = container?.querySelector('.js-productGallery-item[data-index="0"]');
        const image = slide?.querySelector('img');
        const template = this.querySelector('[data-photochromic-overlay]');
        if (!image || !template) return;

        const { Photochromic } = await import(this.dataset.moduleUrl);
        if (disposed) return;
        const opacity = this.dataset.opacity.trim() === '' ? DEFAULT_OPACITY : Number(this.dataset.opacity);
        const overlay = template.content.firstElementChild.cloneNode(true);
        slide.append(overlay);
        cleanups.push(() => overlay.remove());
        const effect = new Photochromic(gallery, {
          enabled: true,
          mask: this.dataset.mask,
          duration: 4000,
          holdDuration: 800,
          renderMode: 'multiply',
          colorStops: [
            { position: 0, color: this.dataset.color, opacity: 0 },
            { position: 1, color: this.dataset.color, opacity }
          ]
        });
        cleanups.push(() => effect.destroy());
        if (!await effect.ready || disposed) return;

        let swiper = null;
        let transitioning = false;
        let unbindSwiper = () => {};

        // 既存画像の表示矩形に追加レイヤーだけを合わせる。画像や親のCSSは変更しない。
        const align = () => {
          const imageBox = image.getBoundingClientRect();
          const slideBox = slide.getBoundingClientRect();
          const style = getComputedStyle(image);
          overlay.style.left = `${imageBox.left - slideBox.left - slide.clientLeft}px`;
          overlay.style.top = `${imageBox.top - slideBox.top - slide.clientTop}px`;
          overlay.style.width = `${imageBox.width}px`;
          overlay.style.height = `${imageBox.height}px`;
          const fit = ['cover', 'contain'].includes(style.objectFit) ? style.objectFit : '100% 100%';
          overlay.style.setProperty('--photochromic-mask-size', fit);
          overlay.style.setProperty('--photochromic-image-fit', style.objectFit);
          overlay.style.setProperty('--photochromic-image-position', style.objectPosition);
          effect.setImage(image.currentSrc || image.src);
        };
        const sync = () => {
          if (disposed) return;
          const active = Boolean(swiper && !swiper.destroyed && !transitioning &&
            Number(swiper.activeIndex) === 0 && image.complete && image.naturalWidth > 0);
          if (active) align();
          // リサイズ等の同期だけで、手動停止したアニメーションを再開しない。
          if (effect.active !== active) effect.setActive(active);
        };
        const hide = () => {
          transitioning = true;
          effect.setActive(false);
        };
        const finish = () => {
          transitioning = false;
          sync();
        };
        const bindSwiper = () => {
          const next = container.swiper;
          if (!next || next.destroyed || next === swiper) return;
          unbindSwiper();
          swiper = next;
          transitioning = Boolean(swiper.animating);
          const events = [
            ['sliderFirstMove', hide],
            ['transitionStart', hide],
            ['transitionEnd', finish],
            ['touchEnd', () => requestAnimationFrame(() => {
              if (!disposed && !swiper.animating) finish();
            })],
            ['slideChange', sync],
            ['resize', sync],
            ['destroy', () => effect.setActive(false)]
          ];
          events.forEach(([event, handler]) => swiper.on(event, handler));
          const boundSwiper = swiper;
          unbindSwiper = () => events.forEach(([event, handler]) => boundSwiper.off(event, handler));
          sync();
        };
        cleanups.push(() => unbindSwiper());
        listen(image, 'load', () => { align(); sync(); });
        listen(image, 'error', () => effect.setActive(false));
        listen(window, 'resize', () => { bindSwiper(); align(); sync(); });
        if (window.ResizeObserver) {
          const observer = new ResizeObserver(() => { bindSwiper(); align(); sync(); });
          observer.observe(image);
          cleanups.push(() => observer.disconnect());
        }
        // 既存テーマの初期化を待つだけで、Swiperを新規生成・再初期化しない。
        bindSwiper();
        let attempts = 0;
        const timer = setInterval(() => {
          bindSwiper();
          if (swiper || ++attempts >= 120) clearInterval(timer);
        }, 250);
        cleanups.push(() => clearInterval(timer));
      };
      initialize().catch(error => {
        this.cleanup?.();
        console.warn('Photochromic initialization skipped:', error);
      });
    }

    disconnectedCallback() {
      this.cleanup?.();
    }
  }

  if (!customElements.get('photochromic-product')) {
    customElements.define('photochromic-product', PhotochromicProduct);
  }
})();
